/**
 * PlanningSurface — /planning and /planning/dashboard.
 *
 * Default: Commitment cockpit (Authorized / Candidate / Hypothesis).
 * Legacy views via ?view=live | ?view=feasibility.
 */

import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LayoutGrid, LayoutList } from 'lucide-react';
import PlanningCommitmentCockpit from './PlanningCommitmentCockpit';
import PlanningLiveDashboard from './PlanningLiveDashboard';
import PlanningFeasibilityDashboard from './PlanningFeasibilityDashboard';

type LegacyView = 'cockpit' | 'live' | 'feasibility';

function resolveView(raw: string | null): LegacyView {
  if (raw === 'live') return 'live';
  if (raw === 'feasibility' || raw === 'simple' || raw === 'c') return 'feasibility';
  return 'cockpit';
}

export default function PlanningSurface() {
  const [params, setParams] = useSearchParams();
  const view = resolveView(params.get('view'));

  if (view === 'cockpit') {
    return <PlanningCommitmentCockpit />;
  }

  const choose = (next: 'live' | 'feasibility') => {
    setParams(next === 'live' ? { view: 'live' } : { view: 'feasibility' }, { replace: true });
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 pt-3">
        <Link
          to="/planning"
          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          ← Commitment home
        </Link>
        <div
          className="inline-flex border border-charcoal-200 dark:border-[var(--rule)] overflow-hidden text-xs font-medium"
          role="group"
          aria-label="Legacy planning view"
        >
          <button
            type="button"
            onClick={() => choose('feasibility')}
            title="Feasibility answer cards"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
              view === 'feasibility'
                ? 'bg-primary-600 text-white'
                : 'bg-[var(--bg-raised)] dark:bg-transparent text-charcoal-500 hover:text-charcoal-700 dark:text-[var(--text-sec)]'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Feasibility
          </button>
          <button
            type="button"
            onClick={() => choose('live')}
            title="Live items workbench"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors border-l border-charcoal-200 dark:border-[var(--rule)] ${
              view === 'live'
                ? 'bg-primary-600 text-white'
                : 'bg-[var(--bg-raised)] dark:bg-transparent text-charcoal-500 hover:text-charcoal-700 dark:text-[var(--text-sec)]'
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            Live items
          </button>
        </div>
      </div>
      {view === 'live' ? <PlanningLiveDashboard /> : <PlanningFeasibilityDashboard />}
    </div>
  );
}
